import * as yup from 'yup';
import { SmsProviderType } from '@constants/channels';
import { widgetPositionSchemaFields } from '@helpers/widget-position';
import { LOCATIONS_MODE } from '@constants/liveChat';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { isDefaultLiveChatTextComplete } from '@features/ChannelsForms/LiveChatForm/payload';

const channelLinkFields = {
    locationId: yup
        .mixed()
        .nullable()
        .test('required-when-linked', 'validation.required', function (value) {
            const { channelLinkEnabled, locationType } = this.parent;
            return !channelLinkEnabled || locationType === LOCATION_TYPE.BRAND || !!value;
        }),
    brandId: yup
        .mixed()
        .nullable()
        .test('required-when-linked', 'validation.required', function (value) {
            const { channelLinkEnabled, locationType } = this.parent;
            return !channelLinkEnabled || locationType !== LOCATION_TYPE.BRAND || !!value;
        }),
};

export const SmsSchema = yup.object().shape({
    ...channelLinkFields,
    name: yup.string().required('validation.required'),
    senderId: yup.string().when('provider', {
        is: provider =>
            provider !== SmsProviderType.Twilio &&
            provider !== SmsProviderType.Custom &&
            provider !== SmsProviderType.RingCentral,
        then: schema =>
            schema
                .max(11, 'validation.maxLength')
                .matches(/^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$/, 'validation.onlyAlphanumericSpace')
                .matches(/(?!^\d+$)^.+$/, 'validation.onlyAlphanumericSpace')
                .required('validation.required'),
        otherwise: schema =>
            schema.when(['provider', 'senderId'], {
                is: (provider, senderId) => provider === SmsProviderType.Twilio && senderId && senderId.length > 0,
                then: schema =>
                    schema
                        .max(11, 'validation.maxLength')
                        .matches(/^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$/, 'validation.onlyAlphanumericSpace')
                        .matches(/(?!^\d+$)^.+$/, 'validation.onlyAlphanumericSpace')
                        .notRequired(),
                otherwise: schema => schema.nullable(),
            }),
    }),
    teamId: yup.string().required('validation.required'),
    provider: yup.number().required('validation.required'),
    accountId: yup.string().when('provider', {
        is: SmsProviderType.Twilio,
        then: schema => schema.required('validation.required'),
        otherwise: schema => schema.nullable(),
    }),
    apiKey: yup.string().when('provider', {
        is: SmsProviderType.Twilio,
        then: schema => schema.required('validation.required'),
        otherwise: schema => schema.nullable(),
    }),
    phoneNumber: yup.string().when('provider', {
        is: SmsProviderType.Twilio,
        then: schema => schema.validatePhone('validation.wrongPhoneFormat').required('validation.required'),
        otherwise: schema =>
            schema.when('provider', {
                is: SmsProviderType.RingCentral,
                then: schema => schema.required('validation.required'),
                otherwise: schema => schema.nullable(),
            }),
    }),
    integrationId: yup.string().when('provider', {
        is: SmsProviderType.RingCentral,
        then: schema => schema.required('validation.required'),
        otherwise: schema => schema.nullable(),
    }),
    token: yup.string().when('provider', {
        is: provider => provider === SmsProviderType.TurboSms || provider === SmsProviderType.SmsTo,
        then: schema => schema.required('validation.required'),
        otherwise: schema => schema.nullable(),
    }),
    login: yup.string().when('provider', {
        is: SmsProviderType.Lifecell,
        then: schema => schema.required('validation.required'),
        otherwise: schema => schema.nullable(),
    }),
    password: yup.string().when('provider', {
        is: SmsProviderType.Lifecell,
        then: schema => schema.required('validation.required'),
        otherwise: schema => schema.nullable(),
    }),
});

export const WhatsAppSchema = yup.object().shape({
    ...channelLinkFields,
    name: yup.string().required('validation.required'),
    teamId: yup.string().required('validation.required'),
});

export const WhatsAppBusinessSchema = yup.object().shape({
    ...channelLinkFields,
    name: yup.string().required('validation.required'),
    teamId: yup.string().required('validation.required'),
    phoneNumber: yup.string().required('validation.required'),
    phoneNumberId: yup.string().required('validation.required'),
    phoneNumberName: yup.string().required('validation.required'),
    whatsAppBusinessId: yup.string().required('validation.required'),
});

export const LiveChatSchema = yup.object().shape({
    name: yup.string().required('validation.required'),
    teamId: yup.string().required('validation.required'),
    brandId: yup.string().required('validation.required'),
    assignLocation: yup.boolean(),
    locationsMode: yup.string(),
    locations: yup.array().when(['assignLocation', 'locationsMode'], {
        is: (assignLocation, locationsMode) => !!assignLocation && locationsMode === LOCATIONS_MODE.SELECTED,
        then: schema => schema.min(1, 'validation.required'),
    }),
    notifyAboutMissingConversations: yup.boolean(),
    followUpChannelId: yup.string().when('notifyAboutMissingConversations', {
        is: value => !!value,
        then: schema => schema.required('validation.required'),
        otherwise: schema => schema.nullable(),
    }),
    buttonCount: yup.number(),
    texts: yup.array().test('default-text-complete', 'validation.required', function (texts) {
        return isDefaultLiveChatTextComplete(texts, this.parent);
    }),
    welcomePopupEnabled: yup.boolean(),
    workingHoursEnabled: yup.boolean(),
    ...widgetPositionSchemaFields,
    allowedSites: yup.array().of(yup.string()),
});

export const FacebookSchema = yup.object().shape({
    ...channelLinkFields,
    name: yup.string().required('validation.required'),
    teamId: yup.string().required('validation.required'),
    // The page cannot be changed after creation, so it is only required for new channels
    pageKey: yup.string().when('id', {
        is: id => !id,
        then: schema => schema.required('validation.required'),
        otherwise: schema => schema.nullable(),
    }),
});

export const HostedEmailSchema = yup.object().shape({
    ...channelLinkFields,
    name: yup.string().required('validation.required'),
    teamId: yup.string().required('validation.required'),
    email: yup.string().email('validation.wrongEmailFormat').required('validation.required'),
    enableIncomingEmail: yup.boolean(),
    customReturnPath: yup.boolean(),
    returnPathSubdomain: yup
        .string()
        .nullable()
        .when('customReturnPath', {
            is: true,
            then: schema =>
                schema
                    .required('validation.required')
                    .matches(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i, 'SettingsChannels.returnPathSubdomainInvalid'),
        }),
});

export const EmailSchema = yup.object().shape({
    ...channelLinkFields,
    name: yup.string().required('validation.required'),
    teamId: yup.string().required('validation.required'),
    provider: yup.number().required('validation.required'),
    smtpHost: yup.string().required('validation.required'),
    smtpPort: yup.number().required('validation.required').typeError('validation.required'),
    username: yup.string().required('validation.required'),
    isSSL: yup.boolean().required('validation.required'),
    email: yup.string().email('validation.wrongEmailFormat').required('validation.required'),
    password: yup.string().required('validation.required'),
    enableIncomingEmail: yup.boolean(),
    imapHost: yup.string().when('enableIncomingEmail', {
        is: true,
        then: schema => schema.required('validation.required'),
        otherwise: schema => schema.nullable(),
    }),
    imapPort: yup.number().when('enableIncomingEmail', {
        is: true,
        then: schema => schema.required('validation.required').typeError('validation.required'),
        otherwise: schema => schema.nullable().transform((value, original) => (original === '' ? null : value)),
    }),
    imapUsername: yup.string().nullable(),
    imapPassword: yup.string().nullable(),
    signature: yup.string().nullable(),
});
